import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  type DocumentItem,
  type DocumentStatusFilter,
  fetchMyDocuments,
} from "./api";
import { CustomSelect } from "@/components/ui/CustomSelect";

const PER_PAGE_OPTIONS = [10, 20, 50, 100] as const;

export function MyDocumentsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("es") ? "es-ES" : "en-US";
  const [searchParams, setSearchParams] = useSearchParams();

  const folder_id = searchParams.get("folder_id") || "";
  const statusRaw = (searchParams.get("status") || "") as DocumentStatusFilter;
  const status: DocumentStatusFilter = ["pending", "signed"].includes(statusRaw)
    ? statusRaw
    : "";
  const page = Math.max(1, Number(searchParams.get("page") || "1"));
  const pageSize = (() => {
    const v = Number(searchParams.get("per_page") || "20");
    return PER_PAGE_OPTIONS.includes(v as 10 | 20 | 50 | 100) ? v : 20;
  })();

  const [search, setSearch] = useState("");

  const query = useQuery({
    queryKey: ["docs-my", { folder_id, status, page, pageSize }],
    queryFn: () =>
      fetchMyDocuments({
        page,
        page_size: pageSize,
        folder_id: folder_id || null,
        status,
      }),
  });

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (!("page" in patch)) next.delete("page");
    setSearchParams(next);
  };

  return (
    <div className="my-documents-root">
      <div className="page-header my-docs-header">
        <div>
          <h1 className="page-title">{t("docs.title")}</h1>
          <p className="page-subtitle">{t("docs.subtitle")}</p>
        </div>
      </div>

      {query.isLoading && <p>{t("common.loading", { defaultValue: "Cargando…" })}</p>}
      {query.isError && (
        <p className="flash flash-error">
          {t("common.error", { defaultValue: "Ocurrió un error" })}
        </p>
      )}

      {query.data && (
        <>
          <div className="kpi-grid my-docs-kpis">
            <div className="kpi-card">
              <div className="kpi-label">{t("docs.kpi.pending")}</div>
              <div className="kpi-value">{query.data.page_pending}</div>
              <div className="summary-card-meta">{t("docs.kpi.on_page")}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">{t("docs.kpi.signed")}</div>
              <div className="kpi-value">{query.data.page_signed}</div>
              <div className="summary-card-meta">{t("docs.kpi.on_page")}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">{t("docs.kpi.total")}</div>
              <div className="kpi-value">{query.data.total_count}</div>
              <div className="summary-card-meta">{t("docs.kpi.matching")}</div>
            </div>
          </div>

          <FilterBar
            folders={query.data.folders}
            folderId={folder_id}
            status={status}
            pageSize={pageSize}
            search={search}
            onSearchChange={setSearch}
            onApply={(patch) => updateParams(patch)}
          />

          <ListCard
            data={query.data}
            search={search}
            locale={locale}
            page={page}
            pageSize={pageSize}
            onPageChange={(p) => updateParams({ page: String(p) })}
          />
        </>
      )}
    </div>
  );
}

function FilterBar(props: {
  folders: { id: string; name: string }[];
  folderId: string;
  status: DocumentStatusFilter;
  pageSize: number;
  search: string;
  onSearchChange: (v: string) => void;
  onApply: (patch: Record<string, string | null>) => void;
}) {
  const { t } = useTranslation();

  const reset = () => props.onApply({ folder_id: null, status: null, per_page: null });

  return (
    <div className="card filter-bar my-docs-filter">
      <div className="my-docs-filter-inner">
        <div className="my-docs-filter-form">
          <div className="form-field">
            <label className="form-label">{t("docs.folder")}</label>
            <CustomSelect
              value={props.folderId}
              options={props.folders.map((f) => ({ value: f.id, label: f.name }))}
              onChange={v => props.onApply({ folder_id: v || null })}
              emptyLabel={t("docs.folder_all")}
            />
          </div>

          <div className="form-field">
            <label className="form-label">{t("docs.status")}</label>
            <CustomSelect
              value={props.status}
              options={[
                { value: "pending", label: t("docs.kpi.pending") },
                { value: "signed", label: t("docs.kpi.signed") },
              ]}
              onChange={v => props.onApply({ status: v || null })}
              emptyLabel={t("docs.status_all")}
            />
          </div>

          <div className="form-field">
            <label className="form-label">{t("docs.rows")}</label>
            <CustomSelect
              value={String(props.pageSize)}
              options={PER_PAGE_OPTIONS.map((o) => ({ value: String(o), label: String(o) }))}
              onChange={v => props.onApply({ per_page: v === "20" ? null : v })}
              allowEmpty={false}
            />
          </div>

          <div className="my-docs-filter-actions">
            <button type="button" className="btn btn-secondary" onClick={reset}>
              {t("docs.reset")}
            </button>
          </div>
        </div>

        <div className="my-docs-search">
          <label className="form-label" htmlFor="docSearch">{t("docs.search")}</label>
          <input
            className="form-input"
            id="docSearch"
            type="search"
            placeholder={t("docs.search_placeholder")}
            value={props.search}
            onChange={(e) => props.onSearchChange(e.target.value)}
            autoComplete="off"
          />
          <div className="summary-card-meta">{t("docs.search_hint")}</div>
        </div>
      </div>

      <div className="filter-chips">
        <button
          type="button"
          className={"chip" + (props.status === "" ? " chip-active" : "")}
          onClick={() => props.onApply({ status: null })}
        >
          {t("docs.status_all")}
        </button>
        <button
          type="button"
          className={"chip" + (props.status === "pending" ? " chip-active" : "")}
          onClick={() => props.onApply({ status: "pending" })}
        >
          {t("docs.status_pending")}
        </button>
        <button
          type="button"
          className={"chip" + (props.status === "signed" ? " chip-active" : "")}
          onClick={() => props.onApply({ status: "signed" })}
        >
          {t("docs.status_signed")}
        </button>
      </div>
    </div>
  );
}

function ListCard(props: {
  data: {
    docs: DocumentItem[];
    page_pending: number;
    total_count: number;
    total_pages: number;
  };
  search: string;
  locale: string;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
}) {
  const { t } = useTranslation();
  const { data, search } = props;

  const filteredDocs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data.docs;
    return data.docs.filter((d) => {
      const folderName = d.folder?.name || "";
      return (
        d.title.toLowerCase().includes(term) ||
        folderName.toLowerCase().includes(term)
      );
    });
  }, [data.docs, search]);

  const rangeStart =
    data.total_count === 0 ? 0 : (props.page - 1) * props.pageSize + 1;
  const rangeEnd = Math.min(data.total_count, props.page * props.pageSize);

  return (
    <div className="card my-docs-list-card">
      <div className="documents-table-toolbar">
        <div className="summary-card-meta">
          {t("docs.showing", {
            start: rangeStart,
            end: rangeEnd,
            total: data.total_count,
          })}
        </div>
        {data.total_pages > 1 && (
          <div className="summary-card-meta">
            {t("docs.page_of", { page: props.page, total: data.total_pages })}
          </div>
        )}
      </div>

      {data.docs.length === 0 && (
        <div className="empty-state">{t("docs.empty")}</div>
      )}

      {data.docs.length > 0 && data.page_pending === 0 && (
        <div className="doc-banner doc-banner-success">
          {t("docs.all_caught_up")}
        </div>
      )}

      {data.docs.length > 0 && (
        <div className="doc-list">
          {filteredDocs.length === 0 && (
            <div className="empty-state">{t("docs.no_matches")}</div>
          )}
          {filteredDocs.map((d) => (
            <DocRow key={d.id} doc={d} locale={props.locale} />
          ))}
        </div>
      )}

      {data.total_pages > 1 && (
        <div className="documents-table-toolbar" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={props.page <= 1}
            onClick={() => props.onPageChange(props.page - 1)}
          >
            ← {t("docs.action.back")}
          </button>
          <div className="summary-card-meta">
            {t("docs.page_of", { page: props.page, total: data.total_pages })}
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={props.page >= data.total_pages}
            onClick={() => props.onPageChange(props.page + 1)}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

function DocRow({ doc, locale }: { doc: DocumentItem; locale: string }) {
  const { t } = useTranslation();
  const folderLabel = doc.folder?.name || t("docs.no_folder");
  const isPending = doc.requires_ack && !doc.acknowledged_at;
  const isSigned = doc.requires_ack && !!doc.acknowledged_at;

  return (
    <div className={"doc-row" + (isPending ? " is-pending" : "")}>
      <div className="doc-main">
        <div className="doc-title">{doc.title}</div>
        <div className="doc-meta">
          <span className="doc-folder">{folderLabel}</span>
          <span className="doc-dot">•</span>
          <span className="doc-date">{formatDateTime(doc.created_at, locale)}</span>
        </div>
      </div>

      <div className="doc-status">
        {!doc.requires_ack && (
          <span className="status-pill status-neutral">
            {t("docs.pill.no_signature")}
          </span>
        )}
        {isSigned && (
          <span className="status-pill status-approved">{t("docs.pill.signed")}</span>
        )}
        {isPending && (
          <span className="status-pill status-warning">{t("docs.pill.pending")}</span>
        )}
      </div>

      <div className="doc-actions">
        {isPending ? (
          <Link to={`/documents/${doc.id}`} className="btn btn-primary btn-sm">
            {t("docs.action.review_sign")}
          </Link>
        ) : (
          <Link to={`/documents/${doc.id}`} className="btn btn-primary btn-sm">
            {t("docs.action.open")}
          </Link>
        )}
      </div>
    </div>
  );
}

function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
